class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble, .yula-cart');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
      event.preventDefault();
      this.refresh()
        .then(() => this.open(cartLink))
        .catch((error) => {
          console.error('[Yula cart] Unable to refresh before opening.', error);
          this.open(cartLink);
        });
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  syncCount(source) {
    const count = Number(source.dataset.itemCount);
    this.classList.toggle('is-empty', source.classList.contains('is-empty'));
    if (Number.isFinite(count)) {
      this.dataset.itemCount = String(count);
      document.querySelectorAll('[data-yula-cart-count]').forEach(el => { el.textContent = String(count); });
    }
  }

  applySections(sections) {
    if (typeof sections?.['cart-drawer'] !== 'string') throw new Error('Missing cart drawer section');
    const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const source = html.querySelector('cart-drawer');
    const inner = source?.querySelector('#CartDrawer');
    if (!source || !inner) throw new Error('Invalid cart drawer section');
    this.querySelector('#CartDrawer').innerHTML = inner.innerHTML;
    this.syncCount(source);
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && typeof sections['cart-icon-bubble'] === 'string') {
      const fragment = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
      if (fragment) bubble.innerHTML = fragment.innerHTML;
    }
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    if (this.classList.contains('active')) trapFocus(this.querySelector('.drawer__inner'));
  }

  async refresh() {
    const requestId = (this.refreshRequestId || 0) + 1;
    this.refreshRequestId = requestId;
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(root + '?sections=cart-drawer,cart-icon-bubble', {
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to refresh cart');
    const sections = await response.json();
    if (requestId !== this.refreshRequestId) return;
    this.applySections(sections);
  }

  async renderContents(parsedState) {
    this.productId = parsedState.id;
    this.refreshRequestId = (this.refreshRequestId || 0) + 1;
    try {
      this.applySections(parsedState.sections);
    } catch (error) {
      await this.refresh();
    }
    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
